import { useCallback, useRef, useState } from 'react';
import type { ApMeasurementInput, Floor, Report, SyntheticAp } from '@wrp/shared';
import { createRng, createSyntheticSite, gridPoints, simulateScan, walkPoints } from '@wrp/shared';
import * as api from '../api';
import { RssiBar } from '../components/RssiBar';
import { rssiClass } from '../format';
import { useApp, useSafeAction } from '../store';

type DemoStep = 'start' | 'setup-floor' | 'place-aps' | 'calibrate' | 'walk' | 'results';
/** `idle` = waiting for the user (or a failed step), `running` = the step's work is in flight. */
type Phase = 'idle' | 'running' | 'done';

const STEPS: ReadonlyArray<{ id: DemoStep; label: string }> = [
  { id: 'start', label: 'Start' },
  { id: 'setup-floor', label: 'Floor' },
  { id: 'place-aps', label: 'Access points' },
  { id: 'calibrate', label: 'Calibrate' },
  { id: 'walk', label: 'Walk' },
  { id: 'results', label: 'Results' },
];

/** The same deterministic RF model the device simulator and the accuracy tests use. */
const SITE = createSyntheticSite({ width_m: 30, height_m: 20 });
const CALIBRATION_POINTS = gridPoints(SITE, 10); // 3 x 2 grid = 6 reference points
const SAMPLES_PER_POINT = 2;
const WALK = walkPoints(SITE, 8);
const TOTAL_FINGERPRINTS = CALIBRATION_POINTS.length * SAMPLES_PER_POINT;
const SCALE = 20; // stage pixels per metre

/** Pacing of the walkthrough, in ms. Slow enough to follow every transition. */
const PACE = {
  apPlaced: 950,
  refPointPlaced: 700,
  beforeSample: 450,
  afterSample: 900,
  walkStride: 950,
  afterFix: 750,
};

interface Pt {
  x: number;
  y: number;
}

interface PlacedRef extends Pt {
  id: string;
  samples: number;
}

interface Fix {
  truth: Pt;
  est: Pt;
  error_m: number;
}

interface Scan {
  /** Where the scan was taken, for the panel caption. */
  label: string;
  rssi: Record<string, number>;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function scanOf(at: Pt, rng: () => number, noiseDb = 2): ApMeasurementInput[] {
  return simulateScan(SITE, at.x, at.y, rng, { noiseDb, missProbability: 0 });
}

function rssiMap(aps: ApMeasurementInput[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const ap of aps) {
    if (typeof ap.signal_strength === 'number') out[ap.bssid] = ap.signal_strength;
  }
  return out;
}

function reportOf(deviceId: string, at: Pt, aps: ApMeasurementInput[], floorId?: string): Report {
  return {
    device_id: deviceId,
    source: 'scan',
    scan_ap_num: aps.length,
    aps,
    ...(floorId ? { ground_truth: { floor_id: floorId, x: at.x, y: at.y } } : {}),
  } as Report;
}

export function DemoPage() {
  const { selectFloor, pushToast, refreshFloors, refreshAps, refreshReferencePoints, refreshFingerprints, setEstimate } =
    useApp();
  const safe = useSafeAction();

  const [step, setStep] = useState<DemoStep>('start');
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [note, setNote] = useState('');

  const [floor, setFloor] = useState<Floor | null>(null);
  const [placedAps, setPlacedAps] = useState<SyntheticAp[]>([]);
  const [refPoints, setRefPoints] = useState<PlacedRef[]>([]);
  const [scanAt, setScanAt] = useState<Pt | null>(null);
  const [scan, setScan] = useState<Scan | null>(null);
  const [walker, setWalker] = useState<Pt | null>(null);
  const [fixes, setFixes] = useState<Fix[]>([]);

  const rngRef = useRef(createRng(0x5eed));

  const stepIndex = STEPS.findIndex((s) => s.id === step);
  const meanError = fixes.length ? fixes.reduce((sum, f) => sum + f.error_m, 0) / fixes.length : null;
  const sampleCount = refPoints.reduce((n, p) => n + p.samples, 0);

  // --------------------------------------------------------------- step work

  const createFloorStep = useCallback(async () => {
    setNote(`Creating a ${SITE.width_m} × ${SITE.height_m} m floor…`);
    setProgress(30);
    const created = await safe('Failed to create the demo floor', () =>
      api.createFloor({ name: 'Demo Lab', width_m: SITE.width_m, height_m: SITE.height_m }),
    );
    if (!created) return false;

    setFloor(created);
    selectFloor(created.id);
    await refreshFloors();
    setProgress(100);
    setNote(`“${created.name}” is ready — origin bottom-left, metres.`);
    pushToast('success', 'Demo floor created');
    return true;
  }, [safe, selectFloor, refreshFloors, pushToast]);

  const placeApsStep = useCallback(
    async (target: Floor) => {
      setPlacedAps([]);
      setNote('Scanning from the middle of the floor…');
      setProgress(8);

      // APs only exist once the backend has heard them, so send one clean scan first.
      const centre = { x: SITE.width_m / 2, y: SITE.height_m / 2 };
      const aps = scanOf(centre, rngRef.current, 0);
      const bootstrap = await safe('Failed to register the access points', () =>
        api.createReport(reportOf('demo-rig', centre, aps)),
      );
      if (!bootstrap) return false;

      setScan({ label: `scan from ${centre.x} m, ${centre.y} m`, rssi: rssiMap(aps) });
      setScanAt(centre);
      await sleep(PACE.apPlaced);
      setScanAt(null);

      for (let i = 0; i < SITE.aps.length; i++) {
        const ap = SITE.aps[i]!;
        const saved = await safe(`Failed to place ${ap.ssid}`, () =>
          api.updateApPosition(ap.bssid, { floor_id: target.id, x: ap.x, y: ap.y }),
        );
        if (!saved) return false;

        setPlacedAps((prev) => [...prev, ap]);
        setNote(`${ap.ssid} anchored at ${ap.x} m, ${ap.y} m — heard at ${aps.find((a) => a.bssid === ap.bssid)?.signal_strength ?? '?'} dBm`);
        setProgress(Math.round(((i + 1) / SITE.aps.length) * 100));
        await sleep(PACE.apPlaced);
      }

      await refreshAps();
      setNote(`${SITE.aps.length} access points anchored on the plan.`);
      pushToast('success', `${SITE.aps.length} access points placed`);
      return true;
    },
    [safe, refreshAps, pushToast],
  );

  const calibrateStep = useCallback(
    async (target: Floor) => {
      setRefPoints([]);
      let collected = 0;

      for (let i = 0; i < CALIBRATION_POINTS.length; i++) {
        const pt = CALIBRATION_POINTS[i]!;
        setScanAt(pt);
        setNote(`Standing at ${pt.x} m, ${pt.y} m…`);

        const rp = await safe('Failed to create a reference point', () =>
          api.createReferencePoint({
            floor_id: target.id,
            x: pt.x,
            y: pt.y,
            label: `RP ${pt.x},${pt.y}`,
          }),
        );
        if (!rp) {
          setScanAt(null);
          return false;
        }
        setRefPoints((prev) => [...prev, { id: rp.id, x: pt.x, y: pt.y, samples: 0 }]);
        await sleep(PACE.refPointPlaced);

        for (let s = 0; s < SAMPLES_PER_POINT; s++) {
          const aps = scanOf(pt, rngRef.current);
          setScan({ label: `RP ${pt.x},${pt.y} · sample ${s + 1}/${SAMPLES_PER_POINT}`, rssi: rssiMap(aps) });
          await sleep(PACE.beforeSample);

          const report = await safe('Failed to send a calibration scan', () =>
            api.createReport(reportOf('demo-device', pt, aps, target.id)),
          );
          const reportId = report?.ids[0];
          if (!reportId) {
            setScanAt(null);
            return false;
          }

          const fp = await safe('Failed to store a fingerprint', () =>
            api.createFingerprint(reportId, { reference_point_id: rp.id }),
          );
          if (!fp) {
            setScanAt(null);
            return false;
          }

          collected++;
          setRefPoints((prev) => prev.map((p) => (p.id === rp.id ? { ...p, samples: p.samples + 1 } : p)));
          setNote(`Fingerprint ${collected}/${TOTAL_FINGERPRINTS} stored at ${pt.x} m, ${pt.y} m`);
          setProgress(Math.round((collected / TOTAL_FINGERPRINTS) * 100));
          await sleep(PACE.afterSample);
        }
      }

      setScanAt(null);
      await Promise.all([refreshReferencePoints(), refreshFingerprints()]);
      setNote(`${collected} fingerprints across ${CALIBRATION_POINTS.length} reference points.`);
      pushToast('success', `${collected} fingerprints collected`);
      return true;
    },
    [safe, refreshReferencePoints, refreshFingerprints, pushToast],
  );

  const walkStep = useCallback(
    async (target: Floor) => {
      setFixes([]);
      setWalker(WALK[0] ?? null);
      await sleep(PACE.walkStride);

      const collected: Fix[] = [];
      for (let i = 0; i < WALK.length; i++) {
        const pt = WALK[i]!;
        setWalker(pt);
        setNote(`Walking to ${pt.x.toFixed(1)} m, ${pt.y.toFixed(1)} m…`);
        await sleep(PACE.walkStride);

        const aps = scanOf(pt, rngRef.current);
        setScan({ label: `waypoint ${i + 1}/${WALK.length}`, rssi: rssiMap(aps) });
        setScanAt(pt);
        await sleep(PACE.beforeSample);

        const report = await safe('Failed to send a scan from the walk', () =>
          api.createReport(reportOf('demo-walker', pt, aps, target.id)),
        );
        const reportId = report?.ids[0];
        if (!reportId) {
          setScanAt(null);
          return false;
        }

        const result = await safe('Failed to estimate a position', () =>
          api.estimate({ report_id: reportId, algorithm: 'wknn', k: 4, floor_id: target.id }),
        );
        setScanAt(null);

        if (result?.position) {
          const est = { x: result.position.x, y: result.position.y };
          const error = result.error_m ?? Math.hypot(est.x - pt.x, est.y - pt.y);
          collected.push({ truth: pt, est, error_m: error });
          setFixes((prev) => [...prev, { truth: pt, est, error_m: error }]);
          setEstimate({ reportId, result, at: new Date().toISOString() });
          setNote(`Fix ${collected.length}/${WALK.length} — ${error.toFixed(2)} m from truth`);
        }

        setProgress(Math.round(((i + 1) / WALK.length) * 100));
        await sleep(PACE.afterFix);
      }

      if (!collected.length) {
        setNote('No position could be estimated — the fingerprint database is empty.');
        return false;
      }

      const mean = collected.reduce((sum, f) => sum + f.error_m, 0) / collected.length;
      setNote(`${collected.length} fixes, ${mean.toFixed(2)} m mean error.`);
      pushToast('success', `${collected.length} positions estimated (${mean.toFixed(2)} m mean error)`);
      return true;
    },
    [safe, setEstimate, pushToast],
  );

  // ----------------------------------------------------------- orchestration

  const run = useCallback(async (next: DemoStep, action: () => Promise<boolean>) => {
    setStep(next);
    setPhase('running');
    setProgress(0);
    const ok = await action();
    setPhase(ok ? 'done' : 'idle');
  }, []);

  const goCreateFloor = useCallback(() => void run('setup-floor', createFloorStep), [run, createFloorStep]);
  const goPlaceAps = useCallback(() => {
    if (floor) void run('place-aps', () => placeApsStep(floor));
  }, [run, placeApsStep, floor]);
  const goCalibrate = useCallback(() => {
    if (floor) void run('calibrate', () => calibrateStep(floor));
  }, [run, calibrateStep, floor]);
  const goWalk = useCallback(() => {
    if (floor) void run('walk', () => walkStep(floor));
  }, [run, walkStep, floor]);
  const goResults = useCallback(() => {
    setStep('results');
    setPhase('done');
    setProgress(100);
    setNote('');
  }, []);

  const reset = useCallback(async () => {
    rngRef.current = createRng(Date.now() >>> 0);
    setStep('start');
    setPhase('idle');
    setProgress(0);
    setNote('');
    setFloor(null);
    setPlacedAps([]);
    setRefPoints([]);
    setScanAt(null);
    setScan(null);
    setWalker(null);
    setFixes([]);
    await refreshFloors();
  }, [refreshFloors]);

  /** Re-runs the current step after a failure. */
  const retry = useCallback(() => {
    if (step === 'setup-floor') return goCreateFloor();
    if (!floor) return;
    if (step === 'place-aps') void run('place-aps', () => placeApsStep(floor));
    if (step === 'calibrate') void run('calibrate', () => calibrateStep(floor));
    if (step === 'walk') void run('walk', () => walkStep(floor));
  }, [step, floor, goCreateFloor, run, placeApsStep, calibrateStep, walkStep]);

  const running = phase === 'running';
  const failed = phase === 'idle' && step !== 'start';

  return (
    <div className="demo">
      <div className="panel demo__head">
        <div className="panel__header">
          <h2>Guided demo</h2>
          <span className="muted">
            floor → access points → fingerprints → live positioning, on synthetic RF
          </span>
        </div>

        <ol className="demo-steps">
          {STEPS.map((s, i) => {
            const state = i < stepIndex ? 'is-done' : i === stepIndex ? 'is-active' : 'is-todo';
            const done = i < stepIndex || (i === stepIndex && phase === 'done');
            return (
              <li
                key={s.id}
                className={`demo-step ${state}${running && i === stepIndex ? ' is-running' : ''}`}
                aria-current={i === stepIndex ? 'step' : undefined}
              >
                <span className="demo-step__number">
                  <span className="demo-step__mark" key={done ? 'done' : 'todo'}>
                    {done ? '✓' : i + 1}
                  </span>
                </span>
                <span className="demo-step__label">{s.label}</span>
              </li>
            );
          })}
        </ol>

        {step !== 'start' && (
          <div
            className="demo-progress"
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="demo-progress__track">
              <div
                className={`demo-progress__fill${running ? ' is-running' : ''}`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="demo-progress__value">{progress}%</span>
          </div>
        )}

        <p className="demo-note">
          {running && <span className="demo-spinner" aria-hidden="true" />}
          <span className="demo-note__text" key={note}>
            {note}
          </span>
        </p>
      </div>

      <div className="demo__split">
        <div className="panel demo__stage-panel">
          <div className="panel__header">
            <h2>Floor plan</h2>
            <span className="muted">
              {SITE.width_m} × {SITE.height_m} m
            </span>
            <div className="map-legend panel__actions">
              <span className="legend legend--ap">Access point</span>
              <span className="legend legend--rp">Reference point</span>
              <span className="legend legend--truth">Device</span>
              <span className="legend legend--estimate">Estimate</span>
            </div>
          </div>
          <div className="demo-stage-wrap">
            <DemoStage
              aps={placedAps}
              refPoints={refPoints}
              scanAt={scanAt}
              rssi={scan?.rssi ?? {}}
              walker={walker}
              fixes={fixes}
              showRoute={step === 'walk' || step === 'results'}
            />
          </div>
        </div>

        <div className="demo__side">
          <div className="panel demo-card" key={step}>
            {step === 'start' && (
              <>
                <h2>Everything this platform does, in five steps</h2>
                <p className="muted">
                  The demo drives the real REST API with a deterministic path-loss model, so the floor,
                  access points, fingerprints and position fixes it creates are genuine records you can
                  inspect on the other tabs afterwards.
                </p>
                <ul className="demo-list">
                  <li>
                    Create a {SITE.width_m} × {SITE.height_m} m floor
                  </li>
                  <li>Anchor {SITE.aps.length} access points</li>
                  <li>
                    Fingerprint {CALIBRATION_POINTS.length} reference points ({TOTAL_FINGERPRINTS} samples)
                  </li>
                  <li>Walk {WALK.length} waypoints and estimate each one with weighted KNN</li>
                </ul>
                <button type="button" className="btn btn--primary" onClick={goCreateFloor}>
                  Start the demo
                </button>
              </>
            )}

            {step === 'setup-floor' && (
              <>
                <h2>Step 1 · Floor</h2>
                <p className="muted">
                  Positions are local metres with the origin at the floor plan's bottom-left corner.
                </p>
                {floor && (
                  <dl className="demo-facts">
                    <div>
                      <dt>Name</dt>
                      <dd>{floor.name}</dd>
                    </div>
                    <div>
                      <dt>Extent</dt>
                      <dd>
                        {floor.width_m} × {floor.height_m} m
                      </dd>
                    </div>
                  </dl>
                )}
                <StepFooter
                  phase={phase}
                  failed={failed}
                  onRetry={retry}
                  onNext={goPlaceAps}
                  nextLabel={`Next · place ${SITE.aps.length} access points`}
                />
              </>
            )}

            {step === 'place-aps' && (
              <>
                <h2>Step 2 · Access points</h2>
                <p className="muted">
                  Each AP is learned from a scan, then given coordinates through
                  <code> PATCH /aps/:bssid</code>.
                </p>
                <div className="demo-chips">
                  {placedAps.map((ap, i) => (
                    <span className="chip is-on" key={ap.bssid} style={{ animationDelay: `${i * 40}ms` }}>
                      {ap.ssid} · ch {ap.channel}
                    </span>
                  ))}
                </div>
                <StepFooter
                  phase={phase}
                  failed={failed}
                  onRetry={retry}
                  onNext={goCalibrate}
                  nextLabel={`Next · fingerprint ${CALIBRATION_POINTS.length} points`}
                />
              </>
            )}

            {step === 'calibrate' && (
              <>
                <h2>Step 3 · Calibration</h2>
                <p className="muted">
                  {SAMPLES_PER_POINT} samples at each of {CALIBRATION_POINTS.length} reference points on a
                  10 m grid. Repeated samples are averaged per AP by the fingerprint service.
                </p>
                <dl className="demo-facts">
                  <div>
                    <dt>Reference points</dt>
                    <dd>
                      {refPoints.length} / {CALIBRATION_POINTS.length}
                    </dd>
                  </div>
                  <div>
                    <dt>Fingerprints</dt>
                    <dd>
                      {sampleCount} / {TOTAL_FINGERPRINTS}
                    </dd>
                  </div>
                </dl>
                <StepFooter
                  phase={phase}
                  failed={failed}
                  onRetry={retry}
                  onNext={goWalk}
                  nextLabel={`Next · walk ${WALK.length} waypoints`}
                />
              </>
            )}

            {step === 'walk' && (
              <>
                <h2>Step 4 · Positioning</h2>
                <p className="muted">
                  Each waypoint sends a fresh scan and is located with weighted KNN (k = 4). The dashed
                  red line is the distance between truth and estimate.
                </p>
                <dl className="demo-facts">
                  <div>
                    <dt>Fixes</dt>
                    <dd>
                      {fixes.length} / {WALK.length}
                    </dd>
                  </div>
                  <div>
                    <dt>Mean error</dt>
                    <dd>{meanError === null ? '—' : `${meanError.toFixed(2)} m`}</dd>
                  </div>
                </dl>
                <StepFooter
                  phase={phase}
                  failed={failed}
                  onRetry={retry}
                  onNext={goResults}
                  nextLabel="Next · results"
                />
              </>
            )}

            {step === 'results' && (
              <>
                <h2>Done</h2>
                <div className="demo-metrics">
                  <div className="demo-metric" style={{ animationDelay: '0ms' }}>
                    <div className="demo-metric__value">{placedAps.length}</div>
                    <div className="muted">access points</div>
                  </div>
                  <div className="demo-metric" style={{ animationDelay: '80ms' }}>
                    <div className="demo-metric__value">{sampleCount}</div>
                    <div className="muted">fingerprints</div>
                  </div>
                  <div className="demo-metric" style={{ animationDelay: '160ms' }}>
                    <div className="demo-metric__value">{fixes.length}</div>
                    <div className="muted">position fixes</div>
                  </div>
                  <div className="demo-metric" style={{ animationDelay: '240ms' }}>
                    <div className="demo-metric__value">{meanError === null ? '—' : meanError.toFixed(2)}</div>
                    <div className="muted">m mean error</div>
                  </div>
                </div>
                <p className="muted">
                  The data is still there: <strong>Map</strong> shows the floor with its APs and reference
                  points, <strong>Fingerprints</strong> lists the calibration samples, and
                  <strong> Evaluation</strong> runs k-fold cross validation over them.
                </p>
                <button type="button" className="btn" onClick={() => void reset()}>
                  Run the demo again
                </button>
              </>
            )}
          </div>

          <div className="panel demo-rssi">
            <div className="panel__header">
              <h2>Reported RSSI</h2>
              <span className="muted">{scan ? scan.label : 'no scan yet'}</span>
            </div>
            <ul className="demo-rssi__list">
              {SITE.aps.map((ap) => {
                const dbm = scan?.rssi[ap.bssid];
                return (
                  <li className="demo-rssi__row" key={ap.bssid}>
                    <span className="demo-rssi__name">{ap.ssid}</span>
                    {dbm === undefined ? (
                      <span className="muted demo-rssi__idle">—</span>
                    ) : (
                      <RssiBar dbm={dbm} />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepFooter({
  phase,
  failed,
  onRetry,
  onNext,
  nextLabel,
}: {
  phase: Phase;
  failed: boolean;
  onRetry: () => void;
  onNext: () => void;
  nextLabel: string;
}) {
  if (failed) {
    return (
      <button type="button" className="btn" onClick={onRetry}>
        Retry this step
      </button>
    );
  }
  return (
    <button type="button" className="btn btn--primary" onClick={onNext} disabled={phase !== 'done'}>
      {nextLabel}
    </button>
  );
}

// -------------------------------------------------------------------- stage

interface StageProps {
  aps: SyntheticAp[];
  refPoints: PlacedRef[];
  scanAt: Pt | null;
  rssi: Record<string, number>;
  walker: Pt | null;
  fixes: Fix[];
  showRoute: boolean;
}

function DemoStage({ aps, refPoints, scanAt, rssi, walker, fixes, showRoute }: StageProps) {
  const w = SITE.width_m * SCALE;
  const h = SITE.height_m * SCALE;
  const pad = 36;
  const X = (x: number) => x * SCALE;
  const Y = (y: number) => h - y * SCALE;

  return (
    <svg
      className="demo-stage"
      viewBox={`${-pad} ${-pad} ${w + pad * 2} ${h + pad * 2}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Demo floor plan with access points, reference points and position estimates"
    >
      <defs>
        <pattern id="demo-grid" width={SCALE * 2} height={SCALE * 2} patternUnits="userSpaceOnUse">
          <path
            d={`M ${SCALE * 2} 0 L 0 0 0 ${SCALE * 2}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
        </pattern>
      </defs>

      <rect className="demo-stage__floor" x={0} y={0} width={w} height={h} rx={6} />
      <rect className="demo-stage__grid" x={0} y={0} width={w} height={h} fill="url(#demo-grid)" />

      {showRoute && (
        <polygon className="demo-stage__route" points={WALK.map((p) => `${X(p.x)},${Y(p.y)}`).join(' ')} />
      )}

      {/* One beam per AP while a point is measured; thickness follows the reported RSSI. */}
      {scanAt &&
        aps.map((ap, i) => {
          const dbm = rssi[ap.bssid];
          const strength = dbm === undefined ? 0.3 : Math.min(1, Math.max(0, (dbm + 95) / 55));
          return (
            <line
              key={`beam-${ap.bssid}`}
              className="demo-beam"
              x1={X(ap.x)}
              y1={Y(ap.y)}
              x2={X(scanAt.x)}
              y2={Y(scanAt.y)}
              strokeWidth={0.8 + strength * 2.4}
              opacity={0.18 + strength * 0.5}
              style={{ animationDelay: `${i * 120}ms` }}
            />
          );
        })}

      {refPoints.map((rp) => (
        <g key={rp.id} transform={`translate(${X(rp.x)} ${Y(rp.y)})`}>
          <g className="demo-rp">
            <circle className="demo-rp__ring" r={10} />
            <circle className="demo-rp__dot" r={4.5} />
            {rp.samples > 0 && (
              <text className="demo-rp__count" y={20} key={rp.samples}>
                {rp.samples}
              </text>
            )}
          </g>
        </g>
      ))}

      {scanAt && (
        <g transform={`translate(${X(scanAt.x)} ${Y(scanAt.y)})`}>
          <circle className="demo-scan" r={12} />
          <circle className="demo-scan demo-scan--2" r={12} />
        </g>
      )}

      {fixes.length > 1 && (
        <polyline
          className="demo-stage__trail"
          points={fixes.map((f) => `${X(f.truth.x)},${Y(f.truth.y)}`).join(' ')}
        />
      )}

      {fixes.map((f, i) => (
        <g className="demo-fix" key={`fix-${i}`}>
          <line
            className="demo-fix__error"
            x1={X(f.truth.x)}
            y1={Y(f.truth.y)}
            x2={X(f.est.x)}
            y2={Y(f.est.y)}
          />
          <circle className="demo-fix__dot" cx={X(f.est.x)} cy={Y(f.est.y)} r={5} />
        </g>
      ))}

      {aps.map((ap) => {
        const dbm = rssi[ap.bssid];
        return (
          <g key={ap.bssid} transform={`translate(${X(ap.x)} ${Y(ap.y)})`}>
            <g className="demo-ap">
              <circle className="demo-ap__wave" r={12} />
              <circle className="demo-ap__wave demo-ap__wave--2" r={12} />
              <rect className="demo-ap__dot" x={-6} y={-6} width={12} height={12} rx={2.5} />
              <text className="demo-ap__label" y={-15}>
                {ap.ssid}
              </text>
              <text
                className={`demo-ap__rssi demo-ap__rssi--${dbm === undefined ? 'none' : rssiClass(dbm)}`}
                y={23}
                key={dbm ?? 'none'}
              >
                {dbm === undefined ? '—' : `${dbm} dBm`}
              </text>
            </g>
          </g>
        );
      })}

      {walker && (
        <g className="demo-walker" style={{ transform: `translate(${X(walker.x)}px, ${Y(walker.y)}px)` }}>
          <circle className="demo-walker__halo" r={14} />
          <circle className="demo-walker__dot" r={6} />
        </g>
      )}
    </svg>
  );
}
